import { jsPDF as JsPdf } from 'jspdf';

export const minDatasetValue = (datasets) => {
  if (datasets.length === 0) {
    return null;
  }
  let min = Infinity;
  for (const dataset of datasets) {
    min = dataset.data.reduce((accumulator, point) => {
      if (isNaN(point.y)) {
        return accumulator;
      }
      return Math.min(accumulator, point.y);
    }, min);
  }
  return isFinite(min) ? min : null;
};

export const averageDatasetValue = (datasets) => {
  const extractedValues = datasets.flatMap((dataset) =>
    dataset.data.map((dailyBalance) => dailyBalance.y),
  );

  const numericValues = extractedValues.filter((value) => !isNaN(value));

  if (numericValues.length === 0) {
    return null;
  }

  const average = Math.round(
    numericValues.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0,
    ) / numericValues.length,
  );

  return isFinite(average) ? average : null;
};

export const maxDatasetValue = (datasets) => {
  if (datasets.length === 0) {
    return null;
  }

  let max = -Infinity;
  for (const dataset of datasets) {
    max = dataset.data.reduce((accumulator, point) => {
      if (isNaN(point.y)) {
        return accumulator;
      }
      return Math.max(accumulator, point.y);
    }, max);
  }
  return isFinite(max) ? max : null;
};

export const minGraphValue = (datasets) => {
  if (datasets.length === 0) {
    return 0;
  }

  let graphMin = 0;
  const min = minDatasetValue(datasets);
  if (min < 0) {
    const max = maxDatasetValue(datasets);
    const diff = max - min;
    const padding = diff * 0.1;
    graphMin = Math.round(min - padding);
  }
  return graphMin;
};

export const downloadPDF = (chartCanvasElement, fileName, chartDescription) => {
  fillCanvasBackgroundWithColor(chartCanvasElement);
  const canvasImg = chartCanvasElement.toDataURL('image/jpeg', 1.0);
  const doc = new JsPdf('landscape');
  doc.setFontSize(20);
  doc.text(15, 15, chartDescription);
  doc.addImage(canvasImg, 'JPEG', 10, 10, 280, 150);
  doc.save(fileName);
};

export const downloadChart = (chartCanvasElement, fileName, fileType) => {
  fillCanvasBackgroundWithColor(chartCanvasElement);
  const image = chartCanvasElement.toDataURL(fileType);
  const link = document.createElement('a');
  link.href = image;
  link.download = fileName;
  link.click();
};

export const toggleFullscreen = (chartCanvasElement) => {
  if (isInFullscreenMode()) {
    exitFullscreenMode();
  } else {
    enterFullscreenMode(chartCanvasElement);
  }
};

export const printChart = async (chartCanvasElement) => {
  const win = window.open();

  const img = new Image();
  img.src = chartCanvasElement.toDataURL();
  img.style.width = '100%';

  // Apply CSS styles to ensure chart is centered on the page
  const styleSheet = win.document.createElement('style');
  styleSheet.innerText = `
      body {
        display: flex;
        justify-content: center; /* Horizontal centering */
        align-items: center; /* Vertical centering */
        height: 100vh; /* Ensure the body takes up the full viewport height */
        margin: 0; /* Remove default margins */
      }
    `;

  win.document.head.appendChild(styleSheet);
  win.document.body.appendChild(img);

  await isDocumentReady();

  win.print();
};

const isDocumentReady = async () => {
  return new Promise((resolve) => {
    const stateCheck = setInterval(() => {
      if (document.readyState === 'complete') {
        clearInterval(stateCheck);
        resolve();
      }
    }, 100);
  });
};

const isInFullscreenMode = () => {
  return (
    document.fullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement
  );
};

const enterFullscreenMode = (chartCanvasElement) => {
  if (chartCanvasElement.requestFullscreen) {
    chartCanvasElement.requestFullscreen();
  } else if (chartCanvasElement.msRequestFullscreen) {
    chartCanvasElement.msRequestFullscreen();
  } else if (chartCanvasElement.mozRequestFullScreen) {
    chartCanvasElement.mozRequestFullScreen();
  } else if (chartCanvasElement.webkitRequestFullscreen) {
    chartCanvasElement.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
  }
};

const exitFullscreenMode = () => {
  if (document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.msExitFullscreen) {
    document.msExitFullscreen();
  } else if (document.mozCancelFullScreen) {
    document.mozCancelFullScreen();
  } else if (document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  }
};

const fillCanvasBackgroundWithColor = (chartCanvasElement) => {
  // This method is needed so that the chart background is not grey when downloaded as an image.
  // https://stackoverflow.com/a/50126796

  const chartBackgroundColor = window
    .getComputedStyle(chartCanvasElement)
    .getPropertyValue('background-color');
  // Get the 2D drawing context from the provided canvas.
  const context = chartCanvasElement.getContext('2d');
  context.save();
  context.globalCompositeOperation = 'destination-over';
  context.fillStyle = chartBackgroundColor;
  context.fillRect(0, 0, chartCanvasElement.width, chartCanvasElement.height);
  context.restore();
};
